import type { MediaQueueItem } from '../types/MediaQueueItem';

interface PlayerCanvasProps {
  playbackMode: 'audio' | 'av';
  currentMedia: MediaQueueItem | null;
}

export default function PlayerCanvas({ playbackMode, currentMedia }: PlayerCanvasProps) {
  if (!currentMedia) {
    return (
      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-neutral-950">
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/30 to-neutral-950" />
        <div className="relative z-10 flex flex-col items-center gap-4 text-neutral-500">
          <i className="ri-music-line text-5xl opacity-30" />
          <p className="text-sm font-medium opacity-60">No media available</p>
        </div>
      </div>
    );
  }

  if (currentMedia.type === 'audio') {
    const coverSrc = currentMedia.thumbnail_url ?? '';

    return (
      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-neutral-950">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-950 to-neutral-900" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

        <div className="relative z-10 flex flex-col items-center gap-6 px-8 w-full max-w-lg mx-auto">
          <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 ring-1 ring-white/10 bg-neutral-800">
            {coverSrc ? (
              <img
                src={coverSrc}
                alt={currentMedia.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="ri-music-fill text-5xl text-neutral-600" />
              </div>
            )}
          </div>

          <div className="text-center w-full">
            <h1 className="text-white text-xl md:text-2xl font-bold truncate leading-tight">
              {currentMedia.title}
            </h1>
            {currentMedia.artist && (
              <p className="text-neutral-300 text-sm mt-1.5 truncate">
                {currentMedia.artist}
              </p>
            )}
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-medium">Audio item ready</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentMedia.type === 'image') {
    return (
      <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-neutral-950">
        <img
          src={currentMedia.media_url}
          alt={currentMedia.title}
          className="absolute inset-0 w-full h-full object-contain bg-neutral-950"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 z-10 p-8">
          <div className="max-w-lg mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-3">
              <i className="ri-image-line text-white/70 text-xs" />
              <span className="text-white/70 text-xs font-medium">Image preview</span>
            </div>
            <h1 className="text-white text-xl md:text-2xl font-bold leading-tight">
              {currentMedia.title}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center relative overflow-hidden bg-neutral-950">
      {currentMedia.thumbnail_url && (
        <img
          src={currentMedia.thumbnail_url}
          alt={currentMedia.title}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/80 to-neutral-950" />

      <div className="relative z-10 flex flex-col items-center gap-6 px-8 w-full max-w-lg mx-auto text-center">
        <div className="w-20 h-20 rounded-2xl bg-neutral-800 flex items-center justify-center ring-1 ring-white/10">
          <i className="ri-film-line text-3xl text-neutral-400" />
        </div>

        <div>
          <h1 className="text-white text-xl md:text-2xl font-bold leading-tight">
            {currentMedia.title}
          </h1>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-amber-400 text-xs font-medium">Video item ready</span>
          </div>
          {currentMedia.artist && (
            <p className="text-neutral-400 text-sm mt-3">{currentMedia.artist}</p>
          )}
        </div>
      </div>
    </div>
  );
}